const COMPANY_PREFIX = process.env.REACT_APP_COMPANY_PREFIX || "OPTX";

export const MAX_RECEIPTS = 5;
export const MAX_IMAGE_DIMENSION = 1600;
export const IMAGE_OUTPUT_QUALITY = 0.82;

export const createEmptyTravelExpenseForm = () => ({
  origin: "",
  destination: "",
  departureDate: null,
  departureTime: "10:00",
  transportationType: "Car",
  note: "",
  useTollway: false,
  tollFee: "",
  tollReceipts: [],
  quotationId: "",
  quotationNumber: "",
  quotationYear: "",
  quotationType: "",
  quotationTitle: "",
  projectName: "",
});

export const buildQuotationDisplayNumber = (quotation) => {
  if (!quotation) return "";

  const documentYear = quotation.documentDate
    ? new Date(quotation.documentDate).getFullYear()
    : new Date().getFullYear();

  return `${COMPANY_PREFIX}(${quotation.type || "M"})-${documentYear}-${quotation.runNumber || ""}`;
};

export const getQuotationPayloadNumber = (quotation) =>
  quotation?.runNumber != null && quotation?.runNumber !== ""
    ? String(quotation.runNumber)
    : "";

export const normalizeTravelExpenseAssetUrl = (apiURL, url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (String(url).startsWith("/")) {
    return `${apiURL.replace(/\/$/, "")}${url}`;
  }

  return `${apiURL.replace(/\/$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

export const normalizeTravelExpenseRecords = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

const isResizableImageFile = (file) =>
  Boolean(file) && String(file.type || "").startsWith("image/");

const readImageFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read image file"));
    reader.readAsDataURL(file);
  });

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image"));
    image.src = src;
  });

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Unable to convert canvas to blob"));
      },
      mimeType,
      quality
    );
  });

const resizeImageFile = async (file) => {
  if (!isResizableImageFile(file)) {
    return file;
  }

  const imageUrl = await readImageFileAsDataUrl(file);
  const image = await loadImageElement(imageUrl);
  const longestSide = Math.max(image.width, image.height);

  if (longestSide <= MAX_IMAGE_DIMENSION) {
    return file;
  }

  const scale = MAX_IMAGE_DIMENSION / longestSide;
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    return file;
  }

  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const outputType =
    file.type === "image/png" ? "image/png" : file.type || "image/jpeg";
  const resizedBlob = await canvasToBlob(
    canvas,
    outputType,
    IMAGE_OUTPUT_QUALITY
  );

  return new File([resizedBlob], file.name, {
    type: resizedBlob.type || outputType,
    lastModified: Date.now(),
  });
};

export const optimizeReceiptFiles = async (files) =>
  Promise.all(
    files.map(async (file) => {
      try {
        return await resizeImageFile(file);
      } catch (error) {
        console.error("Receipt resize error:", error);
        return file;
      }
    })
  );

export const canEditTravelExpenseRecord = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus === "pending" || normalizedStatus === "rejected";
};

export const buildEstimateFromRecord = (record, isTollOnlyRequester) => {
  if (!record || isTollOnlyRequester) return null;

  return {
    origin: record.origin || "",
    destination: record.destination || "",
    distanceKm: Number(record.distanceKm || 0),
    amount: Number(record.amount || 0),
    estimatedAmount: Number(record.amount || 0),
    duration: record.duration || "",
    ratePerKm: record.ratePerKm ?? null,
  };
};

export const mapRecordToTravelExpenseFormData = (record, getReceiptUrls) => {
  const departureDate = record?.departureDateTime
    ? new Date(record.departureDateTime)
    : null;

  return {
    origin: record?.origin || "",
    destination: record?.destination || "",
    departureDate:
      departureDate && !Number.isNaN(departureDate.getTime()) ? departureDate : null,
    departureTime:
      departureDate && !Number.isNaN(departureDate.getTime())
        ? `${String(departureDate.getHours()).padStart(2, "0")}:${String(
            departureDate.getMinutes()
          ).padStart(2, "0")}`
        : "10:00",
    transportationType: record?.transportationType || "Car",
    note: record?.note || "",
    useTollway:
      Boolean(record?.useExpressway || record?.useTolls) ||
      Number(record?.tollFee || 0) > 0 ||
      getReceiptUrls(record).length > 0,
    tollFee:
      record?.tollFee != null && record?.tollFee !== ""
        ? String(record.tollFee)
        : "",
    tollReceipts: [],
    quotationId: record?.quotationId || "",
    quotationNumber: record?.quotationNumber || "",
    quotationYear:
      record?.quotationYear != null && record?.quotationYear !== ""
        ? String(record.quotationYear)
        : "",
    quotationType: record?.quotationType || "",
    quotationTitle: record?.quotationTitle || "",
    projectName: record?.projectName || "",
  };
};

export const validateTravelExpenseForm = ({
  formData,
  existingReceiptPreviews,
  maxReceipts = MAX_RECEIPTS,
}) => {
  if (!formData.quotationId) {
    return "กรุณาเลือก QT / โครงการที่ต้องการอ้างอิง";
  }

  if (!formData.origin.trim()) {
    return "กรุณากรอกต้นทาง";
  }

  if (!formData.destination.trim()) {
    return "กรุณากรอกปลายทาง";
  }

  if (!formData.departureDate) {
    return "กรุณาเลือกวันที่เดินทาง";
  }

  if (!formData.departureTime) {
    return "กรุณาเลือกเวลาออกเดินทาง";
  }

  if (!formData.note.trim()) {
    return "กรุณากรอกหมายเหตุ";
  }

  if (formData.useTollway) {
    if (formData.tollFee === "" || Number(formData.tollFee) < 0) {
      return "กรุณากรอกค่าทางด่วนให้ถูกต้อง";
    }

    if (formData.tollReceipts.length > maxReceipts) {
      return `อัปโหลดใบเสร็จทางด่วนได้สูงสุด ${maxReceipts} รูป`;
    }

    if (
      formData.tollReceipts.length === 0 &&
      existingReceiptPreviews.length === 0
    ) {
      return "กรุณาอัปโหลดรูปหรือไฟล์ใบเสร็จทางด่วน";
    }
  }

  return "";
};

export const buildTravelExpenseDepartureDateTime = (departureDate, departureTime) => {
  const [hours, minutes] = departureTime.split(":");
  const departureDateTime = new Date(departureDate);
  departureDateTime.setHours(Number(hours), Number(minutes), 0, 0);
  return departureDateTime.toISOString();
};
